import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  OTP_LENGTH,
  type LoginResponse,
  type OtpPurpose,
  type SendOtpResponse,
  type VerifyOtpResponse,
} from "@mubin/contracts";
import { api, ApiError, storeRole } from "../lib/api";
import { PhoneField } from "../components/phone-field";

/**
 * Sign-in by phone (ADR 0019): a code sent by WhatsApp, or the password chosen after the first code.
 * A first sign-in chooses a password; «نسيت كلمة المرور» sets a new one with a code.
 */
type Step =
  | { kind: "phone"; mode: "code" | "password" }
  | { kind: "code"; purpose: OtpPurpose }
  | { kind: "first-password" };

function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return "رقم الجوال أو كلمة المرور غير صحيحة.";
    if (err.body?.error.message) return err.body.error.message;
  }
  return "تعذر الاتصال بالخادم. تحقق من الاتصال وحاول مرة أخرى.";
}

export function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>({ kind: "phone", mode: "code" });
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (err) {
      const retry = (err as ApiError).body?.error.details as { retryAfter?: number } | undefined;
      if (retry?.retryAfter) setWait(retry.retryAfter);
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function enter() {
    storeRole(null);
    qc.clear();
    await navigate({ to: "/" });
  }

  const sendCode = (purpose: OtpPurpose) =>
    run(async () => {
      const r = await api<SendOtpResponse>("/v1/auth/otp/send", {
        method: "POST",
        json: { phone: phone.trim(), purpose },
      });
      setWait(r.retryAfter);
      setCode("");
      setStep({ kind: "code", purpose });
    });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (step.kind === "phone" && step.mode === "code") return void sendCode("login");
    if (step.kind === "phone") {
      return void run(async () => {
        await api<LoginResponse>("/v1/auth/login", {
          method: "POST",
          json: { phone: phone.trim(), password },
        });
        await enter();
      });
    }
    if (step.kind === "code" && step.purpose === "login") {
      return void run(async () => {
        const r = await api<VerifyOtpResponse>("/v1/auth/otp/verify", {
          method: "POST",
          json: { phone: phone.trim(), code },
        });
        if (r.needsPassword) {
          setPassword("");
          setConfirm("");
          setStep({ kind: "first-password" });
        } else await enter();
      });
    }
    if (step.kind === "code") {
      return void run(async () => {
        await api("/v1/auth/password/reset", {
          method: "POST",
          json: { phone: phone.trim(), code, password },
        });
        setPassword("");
        setStep({ kind: "phone", mode: "password" });
        setError(null);
      });
    }
    return void run(async () => {
      await api("/v1/auth/password/first", { method: "POST", json: { password } });
      await enter();
    });
  }

  const phoneOk = phone.replace(/\D/g, "").length >= 9;
  const passwordsOk = password.length >= 8 && password === confirm;
  const codeOk = new RegExp(`^[0-9]{${OTP_LENGTH}}$`).test(code);
  const ready =
    step.kind === "phone"
      ? phoneOk && (step.mode === "code" ? wait <= 0 : password.length >= 8)
      : step.kind === "code"
        ? codeOk && (step.purpose === "login" || passwordsOk)
        : passwordsOk;

  const title =
    step.kind === "first-password"
      ? "اختر كلمة مرور"
      : step.kind === "code" && step.purpose === "reset"
        ? "كلمة مرور جديدة"
        : "بوابة الإدارة";
  const sub =
    step.kind === "first-password"
      ? "أول دخول لك: اختر كلمة مرور تدخل بها لاحقًا، أو ادخل كل مرة برمز الواتساب."
      : step.kind === "code"
        ? `أرسلنا رمزًا من ${OTP_LENGTH} أرقام إلى واتساب ${phone.trim()}`
        : "للمشرفين ومديري المجمعات وإدارة الجمعية";

  return (
    <main className="login">
      <div className="login-brand">
        <img src="/logo-dark.png" alt="مُبين" />
        <div className="org-by">
          <span>تابع لـ</span>
          <img src="/org/horizontal-white.png" alt="الجمعية الخيرية لتحفيظ القرآن الكريم بمحافظة رماح" />
        </div>
      </div>
      <div className="login-form">
        <form className="login-card" onSubmit={submit} noValidate>
          <div>
            <h1>{title}</h1>
            <p className="sub">{sub}</p>
          </div>
          {error && (
            <div className="alert" role="alert">
              {error}
            </div>
          )}

          {step.kind === "phone" && (
            <>
              <div className="login-tabs" role="tablist" aria-label="طريقة الدخول">
                {(["code", "password"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="tab"
                    aria-selected={step.mode === m}
                    className={step.mode === m ? "on" : ""}
                    onClick={() => {
                      setError(null);
                      setStep({ kind: "phone", mode: m });
                    }}
                  >
                    {m === "code" ? "رمز عبر الواتساب" : "كلمة المرور"}
                  </button>
                ))}
              </div>
              <div className="field">
                <label htmlFor="phone">رقم الجوال</label>
                <PhoneField id="phone" value={phone} onChange={setPhone} required />
              </div>
              {step.mode === "password" && (
                <div className="field">
                  <label htmlFor="password">كلمة المرور</label>
                  <input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    dir="ltr"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              )}
            </>
          )}

          {step.kind === "code" && (
            <div className="field">
              <label htmlFor="code">رمز التحقق</label>
              <input
                id="code"
                className="otp-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                dir="ltr"
                maxLength={OTP_LENGTH}
                placeholder={"•".repeat(OTP_LENGTH)}
                value={code}
                onChange={(e) =>
                  setCode(
                    e.target.value
                      .replace(/[^0-9٠-٩]/g, "")
                      .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660)),
                  )
                }
                autoFocus
                required
              />
            </div>
          )}

          {(step.kind === "first-password" || (step.kind === "code" && step.purpose === "reset")) && (
            <>
              <div className="field">
                <label htmlFor="new-password">كلمة المرور</label>
                <input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  dir="ltr"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <span className="hint">8 أحرف على الأقل</span>
              </div>
              <div className="field">
                <label htmlFor="confirm-password">تأكيد كلمة المرور</label>
                <input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  dir="ltr"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
                {confirm && password !== confirm && <span className="hint">كلمتا المرور غير متطابقتين</span>}
              </div>
            </>
          )}

          <button className="btn btn-primary" type="submit" disabled={busy || !ready}>
            {busy
              ? "لحظة…"
              : step.kind === "phone"
                ? step.mode === "code"
                  ? wait > 0
                    ? `أرسل الرمز بعد ${wait} ث`
                    : "أرسل الرمز"
                  : "دخول"
                : step.kind === "code"
                  ? step.purpose === "login"
                    ? "تحقق وادخل"
                    : "احفظ كلمة المرور"
                  : "احفظ وادخل"}
          </button>

          <div className="login-links">
            {step.kind === "phone" && step.mode === "password" && (
              <button
                type="button"
                className="link"
                disabled={!phoneOk || busy || wait > 0}
                onClick={() => void sendCode("reset")}
              >
                نسيت كلمة المرور؟
              </button>
            )}
            {step.kind === "code" && (
              <>
                <button
                  type="button"
                  className="link"
                  disabled={busy || wait > 0}
                  onClick={() => void sendCode(step.purpose)}
                >
                  {wait > 0 ? `إعادة الإرسال بعد ${wait} ث` : "أعد إرسال الرمز"}
                </button>
                <button
                  type="button"
                  className="link"
                  onClick={() => {
                    setError(null);
                    setStep({ kind: "phone", mode: step.purpose === "login" ? "code" : "password" });
                  }}
                >
                  تغيير الرقم
                </button>
              </>
            )}
            {step.kind === "first-password" && (
              <button type="button" className="link" onClick={() => void enter()}>
                لاحقًا
              </button>
            )}
          </div>
        </form>
      </div>
    </main>
  );
}
